// @ts-nocheck
import React, { useState } from "react";
import { format, parseISO } from "date-fns";

import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SearchIcon from "@material-ui/icons/Search";
import WarningIcon from "@material-ui/icons/Warning";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

import api from "../../../services/api";
import { useRequirement } from "../../../hooks/requirement";
import { useToast } from "../../../hooks/toast";
import { isAtrasoLongo, isPlanoComoEssencial } from "../../../helpers/process.helper";

import { descricaoDocumento } from "./Documentacao";
import {
  Corpo,
  Selo,
  Titulo,
  Aviso,
  Blocos,
  Bloco,
  Progresso,
  Botao,
  BotaoLink,
  Secao,
  Itens,
  Item,
  Recolhido,
  Ajuda,
  diasDaEtapa,
  linkWhatsApp,
} from "./ui";

const ddmm = (data) => (data ? format(parseISO(data), "dd/MM") : null);

// Análise dos documentos (complete_documentation).
export default function Analise({ process }) {
  const requerente = process?.applicant?.name;
  const documentos = (process?.service_documents || []).filter((d) => d?.status !== "NOT_REQUIRED");
  const dias = diasDaEtapa(process?.start_analysis_prediction_date, process?.end_analysis_prediction_date);
  const muitoAtrasada = dias?.atrasado && isAtrasoLongo(process?.end_analysis_prediction_date);
  const previsao = ddmm(process?.end_analysis_prediction_date);
  const essencial = isPlanoComoEssencial(process?.sale?.family_plan);

  // Com muitos documentos, mostra os 3 primeiros e recolhe o resto.
  const visiveis = documentos.slice(0, 3);
  const resto = documentos.slice(3);
  const itemDoc = (doc) => (
    <Item
      key={`${doc?.reference_id}-${doc?.name}`}
      tipo="neutro"
      icone={<SearchIcon />}
      titulo={doc?.name}
      descricao={descricaoDocumento(doc, requerente)}
    />
  );

  return (
    <Corpo>
      <Selo tipo="equipe" />
      {dias?.atrasado ? (
        <>
          <Titulo>A análise está levando mais tempo</Titulo>
          <p className="desc">Nossa equipe já está acompanhando este pedido. Assim que a análise terminar, você recebe um aviso aqui na Central.</p>
          <Aviso tipo="cliente">
            <p>
              <b>Passou do prazo previsto.</b>{" "}
              {muitoAtrasada ? "Pedimos desculpas pela demora; avisamos assim que terminar." : "Pedimos desculpas pela demora; a nova previsão está abaixo."}
            </p>
          </Aviso>
          <Blocos>
            <Bloco rotulo="Previsão inicial">
              <s>{previsao}</s>
            </Bloco>
            <Bloco rotulo="Nova previsão">{muitoAtrasada ? "A confirmar" : "Até 2 dias úteis"}</Bloco>
          </Blocos>
        </>
      ) : (
        <>
          <Titulo>Estamos conferindo seus documentos</Titulo>
          <p className="desc">Nossa equipe confere cada documento que chegou. Nada a fazer agora: se faltar algo, avisamos aqui e no seu e-mail.</p>
          <Blocos>
            <Bloco rotulo="Quanto demora">Até 10 dias úteis</Bloco>
            <Bloco rotulo="Previsão">{previsao ? `Até ${previsao}` : "A confirmar"}</Bloco>
          </Blocos>
          {dias && <Progresso rotulo={`Dia ${dias.atual} de ${dias.total} da análise`} percentual={dias.percentual} />}
        </>
      )}

      {documentos.length > 0 && (
        <>
          <Secao>Em conferência · {documentos.length}</Secao>
          <Itens>{visiveis.map(itemDoc)}</Itens>
          {resto.length > 0 && (
            <Recolhido rotulo={`Mais ${resto.length} documento${resto.length === 1 ? "" : "s"}`} icone={null}>
              <Itens>{resto.map(itemDoc)}</Itens>
            </Recolhido>
          )}
        </>
      )}

      <BotaoLink secundario href="/registro-de-atividades">
        Acompanhar o histórico do caso
      </BotaoLink>

      <Ajuda
        titulo="O que vem depois"
        passos={[
          essencial
            ? "Com tudo aprovado, montamos o processo para enviar a Portugal."
            : "Com tudo aprovado, os documentos seguem para o apostilamento.",
          "Se algum não for aprovado, ele volta para a lista “Você envia” com o motivo.",
        ]}
      />
    </Corpo>
  );
}

// Obstáculo na análise ou no despacho (blocked, blocked_post).
export function Obstaculo({ process, action }) {
  const { addRequirement } = useRequirement();
  const { addToast } = useToast();
  const [enviando, setEnviando] = useState(false);
  const travas = (process?.blocks || []).filter((b) => b?.show_to_client === true);

  const abrirDocumento = (doc) => {
    addRequirement({
      ...doc,
      open: true,
      proxy_form_path: process?.proxy_form_path,
      process_id: process?.id,
      identifier: process?.identifier,
      proxy_path: process?.proxy_path,
    });
    action && action();
  };

  async function pedirContato() {
    try {
      setEnviando(true);
      await api.post("central/request-new-sale-contact", { process_id: process?.id });
      addToast({ type: "success", title: "Pedido enviado", description: "O seu consultor vai entrar em contato." });
    } catch (err) {
      addToast({ type: "error", title: "Não foi possível enviar", description: "Tente de novo em instantes." });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Corpo>
      <Selo tipo="obstaculo" />
      <Titulo>Encontramos um problema que impede o envio</Titulo>
      {/* Sem trava visível não sabemos o motivo nem o prazo: o texto não promete contato em 72h. */}
      <p className="desc">
        {travas.length
          ? "Nossa equipe está cuidando disso. Veja abaixo o que aconteceu e se precisamos de algo seu."
          : "Este pedido está parado por um obstáculo que nossa equipe está acompanhando. Se precisarmos de algo seu, entraremos em contato."}
      </p>

      {travas.map((trava, i) => {
        const categoria = trava?.solution?.category?.name;
        const aguardando = categoria === "WAITING_ANOTHER_PROCESS";
        return (
          <React.Fragment key={trava?.id || i}>
            <Aviso tipo={aguardando ? "cliente" : "obstaculo"}>
              <p style={{ display: "flex", gap: 8 }}>
                {aguardando ? (
                  <AccessTimeIcon style={{ color: "var(--atencao-50)", fontSize: 18, flexShrink: 0 }} />
                ) : (
                  <WarningIcon style={{ color: "var(--erro-50)", fontSize: 18, flexShrink: 0 }} />
                )}
                <span>
                  <b>{trava?.reason?.public_name || (aguardando ? "Aguardando procedimento" : "O que aconteceu")}</b>
                  {trava?.reason?.description ? `: ${trava.reason.description}` : ""}
                </span>
              </p>
            </Aviso>
            {categoria === "INFO_DOCUMENT" && trava?.document && (
              <Botao type="button" onClick={() => abrirDocumento(trava.document)}>
                {trava.document.name === "Procuração assinada"
                  ? process?.proxy_path
                    ? "Edite e baixe sua procuração"
                    : "Preencher procuração"
                  : "Saiba sobre o formato e como emitir"}
                <ChevronRightIcon />
              </Botao>
            )}
            {categoria === "NEW_SALE" && (
              <Botao type="button" disabled={enviando} onClick={pedirContato}>
                {enviando ? "Enviando…" : "Solicitar contato do consultor"}
              </Botao>
            )}
          </React.Fragment>
        );
      })}

      <Blocos>
        <Bloco rotulo="Quanto demora">Depende da solução</Bloco>
        <Bloco rotulo="Previsão">Avisamos quando resolver</Bloco>
      </Blocos>

      <BotaoLink
        secundario
        href={linkWhatsApp(`Olá, tudo bem? Meu pedido ${process?.service_code || ""} está com um obstáculo, poderia me ajudar?`)}
        target="_blank"
        rel="noreferrer"
      >
        Fale com um especialista
      </BotaoLink>
      <BotaoLink secundario href="/registro-de-atividades">
        Acompanhar o histórico do caso
      </BotaoLink>

      <Ajuda
        titulo="O que vem depois"
        passos={[
          "Resolvemos o problema ou pedimos o que for preciso a você.",
          "Quando destravar, aparece “Obstáculo resolvido” aqui e o pedido volta a andar.",
        ]}
      />
    </Corpo>
  );
}
